import { request } from "@/lib/request";
import { isExtension } from "./client";
import { tokenManager } from "./token-manager";

const API = () => import.meta.env.VITE_BASE_URL;
const TRELLO_NONCE_KEY = "unideck.trello.nonce";
const TRELLO_PENDING_KEY = "unideck.trello.pendingToken";

export const SIGN_IN_PROVIDERS = ["google", "apple", "github", "gitlab"];

const randomNonce = () => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// Provider pages leave the app; the extension keeps its own tab and lets the
// web app finish the flow in a new one.
export const openConnectUrl = (url: string) => {
  if (isExtension) {
    window.open(url, "_blank", "noopener");
  } else {
    window.location.href = url;
  }
};

export const signInWithProvider = (service: string, scopes: string[] = []) => {
  const query =
    service === "google" && scopes.length
      ? `?${new URLSearchParams({ scopes: scopes.join(",") })}`
      : "";
  window.location.href = `${API()}/auth/${service}/connect${query}`;
};

type StartOptions = {
  scopes?: string[];
  reconnectSocialId?: string;
  bitrix24?: { portal: string; clientId: string; clientSecret: string };
};

export const connectUrlFor = async (
  provider: string,
  options: StartOptions = {},
): Promise<string> => {
  const response = await request.post<{ data: { url: string } }>(
    "/auth/connect/start",
    {
      provider,
      ...(options.scopes?.length ? { scopes: options.scopes } : {}),
      ...(options.reconnectSocialId
        ? { reconnectSocialId: options.reconnectSocialId }
        : {}),
      ...(options.bitrix24 ? { bitrix24: options.bitrix24 } : {}),
    },
  );
  return response.data.data.url;
};

export const startConnect = async (
  provider: string,
  options: StartOptions = {},
) => {
  if (provider === "trello") {
    startTrelloConnect();
    return;
  }
  if (!tokenManager.isSignedIn()) {
    if (SIGN_IN_PROVIDERS.includes(provider)) {
      signInWithProvider(provider, options.scopes);
    }
    return;
  }
  openConnectUrl(await connectUrlFor(provider, options));
};

export const reconnectUrlFor = async (
  integrationId: string,
  scopes?: string[],
): Promise<string | undefined> => {
  const response = await request.post("/auth/reconnect", null, {
    params: {
      integrationId,
      ...(scopes?.length ? { scopes: scopes.join(",") } : {}),
    },
  });
  return response?.data?.data?.url;
};

export const startTrelloConnect = () => {
  const nonce = randomNonce();
  localStorage.setItem(TRELLO_NONCE_KEY, nonce);
  openConnectUrl(
    `${API()}/auth/trello/connect?nonce=${encodeURIComponent(nonce)}`,
  );
};

// Trello returns its token in the URL fragment; only accept it when the
// nonce matches the one this browser sent, so a crafted link cannot attach
// someone else's Trello account.
export const acceptTrelloRedirect = (hash: string, search: string) => {
  const token = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  const nonce = new URLSearchParams(search).get("nonce");
  const expected = localStorage.getItem(TRELLO_NONCE_KEY);
  localStorage.removeItem(TRELLO_NONCE_KEY);

  if (!token || !nonce || !expected || nonce !== expected) {
    return null;
  }
  return token;
};

export const finishTrelloConnect = async (token: string) => {
  await request.post("/auth/trello/connect", { token });
};

export const holdTrelloToken = (token: string) => {
  sessionStorage.setItem(TRELLO_PENDING_KEY, token);
};

export const finishPendingTrelloConnect = async (): Promise<boolean> => {
  const token = sessionStorage.getItem(TRELLO_PENDING_KEY);
  if (!token || !tokenManager.isSignedIn()) {
    return false;
  }
  sessionStorage.removeItem(TRELLO_PENDING_KEY);
  try {
    await finishTrelloConnect(token);
    return true;
  } catch {
    return false;
  }
};
